import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { startCase } from 'lodash'
import { Card, CloudinaryFileInput } from 'components'
import { Spinner } from 'lp-components'
import * as Types from 'types'
import * as apiActions from 'api-actions'
import * as flashActions from 'redux-flash'
import { selectors as learningHubSelectors } from '../../global-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import {
  displaySubmitFailure,
  filterUnchanged,
  convertCloudinarySrcUrlToFilename,
  getCloudinaryUploadErrorMessage,
  getCurrentEmployment,
} from 'utils'
import { CLOUD_RESIZE_PRESET } from 'config'

// Canonical (Title Case) values, matching the Teaching Artist portal so a TA's
// pronouns set on their entity profile resolve to the same option here.
const PRONOUN_OPTIONS = ['He/Him/His', 'She/Her/Hers', 'They/Them/Theirs']

function formatPronounLabel(value) {
  if (!value) return ''
  return value
    .split('/')
    .map((part) => startCase(part.trim()))
    .join(' / ')
}

// Pronouns are stored in different casing across portals (the artist portal
// saves Title Case, the educator portal lowercase). Match case-insensitively to
// the canonical option so the <select> displays the saved value; preserve any
// unrecognized value (e.g. "Other") so it isn't silently dropped.
function normalizePronouns(value) {
  if (!value) return ''
  const match = PRONOUN_OPTIONS.find(
    (option) => option.toLowerCase() === value.toLowerCase()
  )
  return match || value
}

function pronounSelectOptions(value) {
  if (
    value &&
    !PRONOUN_OPTIONS.some(
      (option) => option.toLowerCase() === value.toLowerCase()
    )
  ) {
    return [...PRONOUN_OPTIONS, value]
  }
  return PRONOUN_OPTIONS
}

const propTypes = {
  createOrUpdateEducatorUserProfile: PropTypes.func.isRequired,
  currentSchoolId: PropTypes.number.isRequired,
  currentSchoolYear: PropTypes.object,
  flashErrorMessage: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  school: PropTypes.object,
  userDetails: Types.user,
}

const defaultProps = {
  currentSchoolYear: null,
  school: null,
  userDetails: null,
}

function ProfileAvatarPreview({ value, image, remove }) {
  if (!value) return null
  const filename = image?.filename || 'Profile photo'
  return (
    <div className="learning-hub-my-profile__avatar-preview">
      <div className="learning-hub-my-profile__avatar-thumb">
        <img src={value} alt="" />
      </div>
      <div className="learning-hub-my-profile__avatar-meta">
        <p className="learning-hub-my-profile__avatar-filename">{filename}</p>
        <button
          type="button"
          className="learning-hub-my-profile__delete-photo"
          onClick={remove}
        >
          Delete
        </button>
      </div>
    </div>
  )
}

ProfileAvatarPreview.propTypes = {
  value: PropTypes.string,
  image: PropTypes.shape({
    filename: PropTypes.string,
  }),
  remove: PropTypes.func.isRequired,
}

ProfileAvatarPreview.defaultProps = {
  value: '',
  image: null,
}

function formatAccessGroups(userDetails) {
  if (!userDetails?.employments?.length) return '—'
  const labels = userDetails.employments.map((employment) => {
    const name = employment.employable?.name
    const position = employment.position
    if (position && name) return `${position}, ${name}`
    return position || name || ''
  })
  const joined = labels.filter(Boolean).join('; ')
  return joined || '—'
}

function MyProfilePage({
  createOrUpdateEducatorUserProfile,
  currentSchoolId,
  currentSchoolYear,
  flashErrorMessage,
  flashSuccessMessage,
  school,
  userDetails,
}) {
  const profile = userDetails?.profile
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [pronouns, setPronouns] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [avatarImage, setAvatarImage] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!profile) return
    setFirstName(profile.firstName || '')
    setLastName(profile.lastName || '')
    setPronouns(normalizePronouns(profile.pronouns || ''))
    setAvatarUrl(profile.avatarUrl || '')
    if (profile.avatarUrl) {
      setAvatarImage({
        src: profile.avatarUrl,
        filename: convertCloudinarySrcUrlToFilename(profile.avatarUrl),
      })
    } else {
      setAvatarImage(null)
    }
  }, [
    profile?.id,
    profile?.firstName,
    profile?.lastName,
    profile?.pronouns,
    profile?.avatarUrl,
  ])

  const handleAvatarRemove = useCallback(() => {
    setAvatarUrl('')
    setAvatarImage(null)
    flashSuccessMessage(
      'Profile picture removed. Select Save to apply your changes.'
    )
  }, [flashSuccessMessage])

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault()
      if (!profile || !currentSchoolYear?.id) {
        flashErrorMessage('Unable to save. Please try again later.')
        return
      }

      setSubmitting(true)
      try {
        const params = {
          firstName,
          lastName,
          pronouns,
          avatarUrl,
          yearsOfExperience: profile.yearsOfExperience,
          disciplines: filterUnchanged({
            initial: profile.disciplines || [],
            new: profile.disciplines || [],
          }),
          applicableGrades: filterUnchanged({
            initial: profile.grades || [],
            new: profile.grades || [],
          }),
        }
        await createOrUpdateEducatorUserProfile(
          profile.id,
          currentSchoolYear.id,
          params
        )
        flashSuccessMessage('Profile updated successfully!')
      } catch (error) {
        displaySubmitFailure(error)
      } finally {
        setSubmitting(false)
      }
    },
    [
      avatarUrl,
      createOrUpdateEducatorUserProfile,
      currentSchoolYear,
      firstName,
      flashErrorMessage,
      flashSuccessMessage,
      lastName,
      profile,
      pronouns,
    ]
  )

  const currentEmployment = userDetails
    ? getCurrentEmployment({
        employments: userDetails.employments,
        currentEmployableId: currentSchoolId,
        currentEmployableType: Types.EMPLOYABLE_TYPES.SCHOOL,
      })
    : null

  const schoolOrgName =
    currentEmployment?.employable?.name || school?.name || '—'
  const contactEmail = userDetails?.email || '—'
  const accessGroupsText = userDetails ? formatAccessGroups(userDetails) : '—'
  const regionDisplay =
    school?.region ||
    school?.detail?.region ||
    school?.detail?.regionName ||
    '—'

  if (!userDetails || !currentSchoolYear) {
    return <Spinner />
  }

  if (!profile) {
    return (
      <Card>
        <p>
          We could not load your profile. Please complete your profile in your
          portal, then return here.
        </p>
      </Card>
    )
  }

  return (
    <>
      <header className="learning-hub-my-profile__page-header">
        <h1>My Profile</h1>
      </header>

      <Card className="learning-hub-my-profile__card">
        <h2 className="learning-hub-my-profile__card-title">Profile Details</h2>
        <form className="learning-hub-my-profile__form" onSubmit={handleSubmit}>
          <div className="learning-hub-my-profile__row learning-hub-my-profile__row--4">
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">
                First name<span aria-hidden>*</span>
              </span>
              <input
                type="text"
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </label>
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">
                Last name<span aria-hidden>*</span>
              </span>
              <input
                type="text"
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </label>
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">Pronouns</span>
              <select
                name="pronouns"
                value={pronouns}
                onChange={(e) => setPronouns(e.target.value)}
              >
                <option value="">Select pronouns</option>
                {pronounSelectOptions(pronouns).map((option) => (
                  <option key={option} value={option}>
                    {formatPronounLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">
                School/ Org/ Teaching Artist Entity
              </span>
              <input type="text" value={schoolOrgName} readOnly tabIndex={-1} />
            </label>
          </div>

          <div className="learning-hub-my-profile__row learning-hub-my-profile__row--3">
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">
                Contact email<span aria-hidden>*</span>
              </span>
              <input type="text" value={contactEmail} readOnly tabIndex={-1} />
            </label>
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">
                Access group(s)
              </span>
              <input
                type="text"
                value={accessGroupsText}
                readOnly
                tabIndex={-1}
              />
            </label>
            <label className="learning-hub-my-profile__field">
              <span className="learning-hub-my-profile__label">Region</span>
              <input type="text" value={regionDisplay} readOnly tabIndex={-1} />
            </label>
          </div>

          <div className="learning-hub-my-profile__avatar-block">
            <span className="learning-hub-my-profile__label">
              Profile picture
            </span>
            <p className="learning-hub-my-profile__hint">
              Accepted formats include png, jpg and jpeg. Your file must be less
              than 10 MB and will be cropped to be a square when uploaded.
            </p>
            <CloudinaryFileInput
              label={false}
              className="learning-hub-my-profile__file-input"
              input={{
                name: 'avatarUrl',
                value: avatarUrl,
                onChange: setAvatarUrl,
              }}
              meta={{}}
              accept=".png,.jpg,.jpeg"
              uploadPath={`/schools/users/profile-pictures/${profile.id}`}
              uploadPreset={CLOUD_RESIZE_PRESET}
              image={avatarImage}
              remove={handleAvatarRemove}
              previewComponent={ProfileAvatarPreview}
              onUploadSuccess={(res) => {
                const url = res.secureUrl || res.secure_url || res.url
                if (!url) return
                const filename = convertCloudinarySrcUrlToFilename(url)
                setAvatarImage({ src: url, filename })
                flashSuccessMessage(
                  'Profile picture uploaded. Select Save to apply your changes.'
                )
              }}
              onUploadFailure={(err) => {
                const message = getCloudinaryUploadErrorMessage(err)
                flashErrorMessage(message)
              }}
            />
          </div>

          <div className="learning-hub-my-profile__actions">
            <button
              type="submit"
              className="button-primary learning-hub-my-profile__save"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Card>
    </>
  )
}

MyProfilePage.propTypes = propTypes
MyProfilePage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentSchoolId: learningHubSelectors.currentSchoolId(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    school: learningHubSelectors.school(state),
    userDetails: learningHubSelectors.userDetails(state),
  }
}

const mapDispatchToProps = {
  createOrUpdateEducatorUserProfile:
    apiActions.createOrUpdateEducatorUserProfile,
  flashErrorMessage: flashActions.flashErrorMessage,
  flashSuccessMessage: flashActions.flashSuccessMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  MyProfilePage
)
